/**
 * activationDisplay — les dérivations d'affichage de l'activation, pour TOUTES les surfaces qui
 * les lisent : le portail Abonnement, « Activer la suite » et le bloc « À faire » de l'accueil.
 *
 * Port littéral de `common/utils/activationSuiteDisplay.js` (mêmes noms, mêmes règles), plus les
 * helpers que ses appelants lui empruntent : `common/utils/partialMonthHours.js` et
 * `common/utils/selfServeRepriseDisplay.js`. `ports/**` ne peut pas importer `src/common/**`
 * (`src/ports/CLAUDE.md` § Dependency rules), d'où la recopie.
 *
 * Les créneaux font exception : leurs conversions et leur libellé vivent avec la grille, sous
 * `data/utility/constraintsGrid` — une seule copie, lue par le legacy et par les ports. Ils sont
 * ré-exportés ici pour que les appelants n'aient qu'une adresse. L'élision du prénom (`elide`)
 * n'est pas ici non plus : elle vit dans `ui/formatters/childFormatter`, où le design system la lit.
 *
 * ⚠️ Deux sources de vérité tant que d'autres parcours legacy (inscription, stage, été) lisent
 * les originaux : une évolution d'une règle commune se reporte ici. **Une seule** copie côté
 * ports, dans ce fichier : trois portages parallèles avaient déjà divergé.
 *
 * Valeurs brutes en entrée, libellés en sortie. Aucune forme d'API ne rentre — c'est le rôle des
 * adaptateurs, qui lisent ces fonctions.
 */
import moment from "moment"
import "moment/locale/fr"

import {
    canonicalSlotsToGridSlots,
    canonicalSlotsToSelectableGridSlots,
    formatSlotsLabel,
    gridDisplayOffsetHours,
    keepSelectableGridSlots,
} from "../../../../../data/utility/constraintsGrid"
import { elide } from "../../../ui/formatters/childFormatter"
import { euros } from "../../../ui/formatters/moneyFormatter"
import { type UISubscriptionLine } from "../../../ui/types"

/**
 * Format long français, millésime inclus — « 1ᵉʳ septembre 2026 ».
 *
 * Locale posée sur l'INSTANCE : la locale globale de moment dépend de l'ordre des imports
 * d'écrans, et le même libellé sortait en « 12 August 2026 » ou « 12 août 2026 » selon le chemin
 * de navigation. En français, SEUL le premier du mois prend l'ordinal. Une date invalide rend
 * `null`, comme une date absente.
 */
export function formatActivationDate(date: moment.MomentInput): string | null {
    const parsed = date ? moment(date).locale("fr") : null
    if (!parsed?.isValid()) return null

    const day = parsed.date() === 1 ? "1ᵉʳ" : `${parsed.date()}`

    return `${day} ${parsed.format("MMMM YYYY")}`
}

/**
 * Montant en euros, sans centimes — « 119 € ». Un montant absent rend « — » plutôt que « 0 € »,
 * qui se lirait comme la gratuité.
 */
export function formatActivationPrice(amount: number | null | undefined): string {
    return amount == null ? "—" : euros(amount)
}

/**
 * Le mot de l'ÉCHÉANCE - « rentrée » pour un produit d'appel saisonnier, « reprise » sinon, et
 * « suite » après un stage tenu pendant l'année (`held_in_year`) : ses cours reprennent le lundi
 * qui le suit, aucune rentrée n'est en jeu. Ne vaut que pour l'accroche : tout ce qui parle des
 * CRÉNEAUX dit « reprise ».
 */
export function activationSeasonWord(type: string | null | undefined, heldInYear = false): string {
    if (heldInYear) return "suite"

    return type === "cours_ete" || type === "stage" ? "rentrée" : "reprise"
}

/** « On prépare la rentrée de Léa ». Prénom absent → « votre enfant » (vouvoiement). */
export function getActivationHeadline(
    type: string | null | undefined,
    firstName: string | null | undefined,
    heldInYear = false
): string {
    return `On prépare la ${activationSeasonWord(type, heldInYear)} ${elide(firstName || "votre enfant")}`
}

/** « votre avantage rentrée » - après un stage de l'année, l'avantage se nomme : la remise fidélité. */
export function activationAdvantageLabel(type: string | null | undefined, heldInYear = false): string {
    return heldInYear ? "votre remise fidélité" : `votre avantage ${activationSeasonWord(type)}`
}

/** La classe demandée au composeur : celle de la rentrée, ou après un stage de l'année, celle d'aujourd'hui. */
export function activationClassTitle(heldInYear = false): string {
    return heldInYear ? "Classe" : "Classe à la rentrée"
}

/** L'accroche de la reprise d'une famille PARTIE — `common/utils/selfServeRepriseDisplay.js`. */
export function getRepriseHero(
    season: string | null | undefined,
    firstName: string | null | undefined
): { headline: string; subline: string } {
    const isSummer = season === "summer"

    return {
        headline: `Reprenez l'accompagnement ${elide(firstName || "votre enfant")}`,
        subline: isSummer
            ? "Les cours d'été sont ouverts : choisissez ses dates et ses matières."
            : "Choisissez sa formule et sa date de reprise.",
    }
}

/** Repli des échecs d'enregistrement — le message du back prime quand il en porte un. */
export const ACTIVATION_SAVE_FAILED_SNACKBAR = "Nous n'avons pas pu enregistrer votre choix. Réessayez dans un instant."

/**
 * « CE QUE VOUS GARDEZ » — les promesses de non-enfermement, mot pour mot.
 *
 * TROIS lignes et non cinq : les quatre droits de changement tiennent en une. « Annulable JUSQU'À
 * LA REPRISE » et surtout pas « à tout moment » : l'offre par défaut est posée avec engagement.
 */
export const ACTIVATION_FLEXIBILITY_POINTS: UISubscriptionLine[] = [
    { icon: "sliders-h", text: "Créneau, formule, matière, tuteur : modifiables à tout moment" },
    { icon: "pause-circle", text: "Jusqu'à 3 mois de pause" },
    { icon: "undo-alt", text: "Annulable jusqu'à la reprise" },
]

/**
 * Ligne de prix UNIQUE — « 1ᵉʳ mois 69 € puis 119 €/mois », ou « 119 €/mois ». La remise n'est
 * annoncée que si elle en est une (1ᵉʳ mois strictement inférieur au mensuel).
 */
export function getActivationPriceLine(
    monthlyPrice: number | null | undefined,
    firstMonthPrice: number | null | undefined
): string | null {
    if (monthlyPrice == null) return null

    const monthly = `${formatActivationPrice(monthlyPrice)}/mois`
    const hasDiscount = firstMonthPrice != null && firstMonthPrice < monthlyPrice

    return hasDiscount ? `1ᵉʳ mois ${formatActivationPrice(firstMonthPrice)} puis ${monthly}` : monthly
}

/** Volume hebdomadaire plancher de la remise fidélité (miroir du back). */
export const LOYALTY_MIN_HOURS_PER_WEEK = 2

export type LoyaltyDiscountInput = {
    /** `planned.loyalty_discount_total` — ce qui a été RÉELLEMENT consenti ; prime sur le barème. */
    grantedAmount?: number | null
    /** `upcoming_activation.loyalty_discount_amount` — le barème courant. */
    catalogAmount?: number | null
    /** `upcoming_activation.discount_window_open` — autorité du back, jamais recalculée ici. */
    windowOpen?: boolean
    inPlaceModification?: boolean
    withCommitment?: boolean
    hoursPerWeek?: number
}

/**
 * Remise fidélité applicable à l'offre EN COURS D'ÉDITION — miroir de
 * `SubscriptionActivationController::store`, pour que le montant annoncé soit celui qui sera
 * facturé. La modification in-place n'accorde jamais de remise nouvelle : seule celle déjà portée
 * par le jalon compte.
 */
export function getLoyaltyDiscount(input: LoyaltyDiscountInput = {}): { eligible: boolean; amount: number } {
    const {
        grantedAmount = 0,
        catalogAmount = 0,
        windowOpen = false,
        inPlaceModification = false,
        withCommitment = false,
        hoursPerWeek = 0,
    } = input

    const granted = Number(grantedAmount) || 0
    const amount = granted > 0 ? granted : Number(catalogAmount) || 0
    const grantable = inPlaceModification ? granted > 0 : granted > 0 || !!windowOpen

    const eligible = grantable && amount > 0 && !!withCommitment && Number(hoursPerWeek) >= LOYALTY_MIN_HOURS_PER_WEEK

    return { eligible, amount: eligible ? amount : 0 }
}

/** La couverture annoncée est-elle DERRIÈRE nous ? Comparaison au JOUR. */
export function isProductCoverageOver(endDate: moment.MomentInput, now?: moment.MomentInput): boolean {
    return !!(endDate && moment(endDate).isBefore(now ? moment(now) : moment(), "day"))
}

/**
 * « se termine le 31 août 2026 » / « s'est terminé le 31 juillet 2026 ».
 *
 * Une fin d'engagement ne borne RIEN (l'abonnement se poursuit au-delà) : ce type ne rend jamais
 * de ligne. Le TEMPS de la phrase suit la date.
 */
export function getProductEndLine(
    type: string | null | undefined,
    endDate: moment.MomentInput,
    now?: moment.MomentInput
): string | null {
    const endLabel = formatActivationDate(endDate)
    if (!endLabel) return null
    if (type === "fin_engagement") return null

    return isProductCoverageOver(endDate, now) ? `s'est terminé le ${endLabel}` : `se termine le ${endLabel}`
}

export type CoverageGap = { hasGap: boolean; startLabel: string | null; endLabel: string | null }

/** Trou entre la fin du produit d'appel et la reprise — la borne haute est la VEILLE de la reprise. */
export function getCoverageGap(productEndDate: moment.MomentInput, resumeDate: moment.MomentInput): CoverageGap {
    const start = productEndDate ? moment(productEndDate).add(1, "day") : null
    const end = resumeDate ? moment(resumeDate).subtract(1, "day") : null
    // `isSameOrAfter` et non `isAfter` : un trou d'un seul jour reste un vrai trou.
    const hasGap = !!(start && end && end.isSameOrAfter(start, "day"))

    if (!hasGap) return { hasGap: false, startLabel: null, endLabel: null }

    return { hasGap: true, startLabel: formatActivationDate(start), endLabel: formatActivationDate(end) }
}

/** Le trou ne s'annonce que tant qu'il est ÉVITABLE. */
export function shouldAnnounceCoverageGap(
    hasGap: boolean,
    productEndDate: moment.MomentInput,
    now?: moment.MomentInput
): boolean {
    return !!hasGap && !isProductCoverageOver(productEndDate, now)
}

/**
 * Compte à rebours de la remise fidélité. Comparaison au JOUR : le dernier jour compte en entier,
 * sinon une remise expirant « aujourd'hui » disparaîtrait dès 00:01.
 */
export function getDiscountUrgency(
    discountExpiresAt: moment.MomentInput,
    now?: moment.MomentInput
): { stillOpen: boolean; deadlineLabel: string | null } {
    const reference = now ? moment(now) : moment()
    const deadline = discountExpiresAt ? moment(discountExpiresAt) : null
    const stillOpen = !!(deadline && deadline.isSameOrAfter(reference, "day"))

    if (!stillOpen) return { stillOpen: false, deadlineLabel: null }

    return { stillOpen: true, deadlineLabel: formatActivationDate(deadline) }
}

/** Miroir du 409 `activation_planned` : on n'offre pas un bouton qui ne peut qu'échouer. */
export function canExtendProduct(extendableTo: string | null | undefined, hasPlannedActivation: boolean): boolean {
    return !!extendableTo && !hasPlannedActivation
}

export type ResumeDateOption = { value: string; adHoc: boolean }

/**
 * Les options de date de reprise : les dates PROPOSÉES par le back, plus, en MODIFICATION, la date
 * du jalon quand elle n'en est pas une — la re-défaulter en silence déplacerait la reprise d'un
 * parent qui ne voulait que changer sa formule.
 *
 * Sous impersonation, `admin_resume_dates` REMPLACE la liste globale (elle en est un sur-ensemble),
 * et seulement si elle est non vide. Les dates proposées PASSÉES sont retirées (le back les refuse
 * au POST) ; la date du JALON ne l'est jamais, même passée : c'est la vérité de la reprise.
 */
export function getResumeDateOptions(input: {
    proposedDates?: (string | null | undefined)[]
    adminDates?: (string | null | undefined)[] | null
    currentDate?: string | null
    now?: string | Date | null
}): { options: ResumeDateOption[]; selected: string | null } {
    const { proposedDates = [], adminDates = null, currentDate = null, now = null } = input

    // Comparaison de chaînes ISO `YYYY-MM-DD`, comme le tri plus bas — même convention.
    const todayIso = (now ? moment(now) : moment()).format("YYYY-MM-DD")
    const granted = (Array.isArray(adminDates) ? adminDates : []).filter(Boolean) as string[]
    const proposed = (granted.length ? granted : ((proposedDates ?? []).filter(Boolean) as string[])).filter(
        (value) => value >= todayIso
    )
    const options: ResumeDateOption[] = proposed.map((value) => ({ value, adHoc: false }))

    if (currentDate && !proposed.includes(currentDate)) {
        options.push({ value: currentDate, adHoc: true })
    }
    options.sort((a, b) => (a.value < b.value ? -1 : a.value > b.value ? 1 : 0))

    return { options, selected: currentDate ?? proposed[0] ?? null }
}

type AllocationEntry = { subject_id?: string | number; hours?: number }
type RawAllocation = AllocationEntry[] | Record<string, number> | null | undefined

function isPositionalAllocation(allocation: object): boolean {
    return Array.isArray(allocation) || Object.prototype.hasOwnProperty.call(allocation, "0")
}

function isAllocationList(allocation: unknown): allocation is AllocationEntry[] {
    return (
        Array.isArray(allocation) &&
        allocation.length > 0 &&
        allocation.every((entry) => entry && typeof entry === "object" && "subject_id" in entry)
    )
}

/**
 * Répartition ramenée à `{subject_id: heures}`, quelle que soit la forme reçue. Une forme
 * positionnelle non nommée est VIDÉE : mieux vaut aucun volume que des volumes faux.
 */
export function allocationBySubject(raw: RawAllocation): Record<string, number> {
    if (isAllocationList(raw)) {
        return raw.reduce<Record<string, number>>((acc, { subject_id: id, hours }) => {
            if (id != null && hours != null) acc[String(id)] = hours
            return acc
        }, {})
    }

    return raw == null ? {} : isPositionalAllocation(raw) ? {} : (raw as Record<string, number>)
}

/**
 * `upcoming_activation` fusionné entre les deux charges que le portail mélange (`/api/user`, qui
 * porte les clés admin, et `/api/v2/subscribe/pricing`, la plus fraîche). La fraîche gagne sur
 * ses propres clés ; le second niveau (`planned`) n'est fusionné que quand les DEUX en portent
 * un — une reprise annulée arrive en `planned: null` et ne doit pas ressusciter.
 */
export function mergeUpcomingActivation<T extends Record<string, unknown>>(
    withAdminKeys: T | null | undefined,
    fresher: T | null | undefined
): T | null {
    if (!withAdminKeys) return fresher ?? null
    if (!fresher) return withAdminKeys

    const merged: Record<string, unknown> = { ...withAdminKeys, ...fresher }

    if (withAdminKeys.planned && fresher.planned) {
        merged.planned = {
            ...(withAdminKeys.planned as Record<string, unknown>),
            ...(fresher.planned as Record<string, unknown>),
        }
    }

    return merged as T
}

export {
    canonicalSlotsToGridSlots,
    canonicalSlotsToSelectableGridSlots,
    formatSlotsLabel,
    gridDisplayOffsetHours,
    keepSelectableGridSlots,
}

/**
 * « Combien d'heures coûte un mois partiel » — miroir de
 * `App\Services\FSM\Subscription\Domain\PartialMonthHoursDomain::hoursFor()`.
 *
 * `round((heures_du_mois × jours_couverts / jours_du_mois) + 0.25)` : le `+0.25` est un biais
 * délibérément à la baisse, pour qu'une famille ne paie jamais une fraction d'heure qu'elle
 * atteint à peine.
 */
export function partialMonthHours(
    startsAt: string | Date | null | undefined,
    hoursPerMonth: number | null | undefined
): number | undefined {
    if (!startsAt || !hoursPerMonth) return undefined

    // `moment(string)` sans format déprécie les formats non ISO.
    const start =
        typeof startsAt === "string" ? moment(startsAt, ["YYYY-MM-DD HH:mm", moment.ISO_8601]) : moment(startsAt)
    if (!start.isValid()) return undefined

    const daysInMonth = start.daysInMonth()
    const coveredDays = start.clone().endOf("month").diff(start, "days") + 1

    return Math.round((hoursPerMonth * coveredDays) / daysInMonth + 0.25)
}
